import { useEffect, useState, useRef } from "react";
import { MapContainer, TileLayer, Marker, Polyline, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { openMapsNavigation } from "../utils/openMapsNavigation";

// Fix iconos por defecto de Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl:       "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl:     "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

function emojiIcon(emoji, bg, size = 30) {
  return L.divIcon({
    html: `<div style="
      background:${bg};color:white;border-radius:50%;
      width:${size}px;height:${size}px;
      display:flex;align-items:center;justify-content:center;
      font-size:${Math.round(size * 0.5)}px;
      border:2.5px solid rgba(255,255,255,0.9);
      box-shadow:0 2px 12px rgba(0,0,0,0.5);
    ">${emoji}</div>`,
    className: "",
    iconSize:   [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

function MapFitter({ routeCoords, origin, destination }) {
  const map = useMap();
  const fitted = useRef(false);

  useEffect(() => {
    if (fitted.current) return;
    try {
      if (routeCoords.length > 1) {
        map.fitBounds(L.latLngBounds(routeCoords), { padding: [40, 40], animate: false });
        fitted.current = true;
      } else if (origin && destination) {
        map.fitBounds(
          L.latLngBounds([[origin.lat, origin.lng], [destination.lat, destination.lng]]),
          { padding: [60, 60], animate: false }
        );
        fitted.current = true;
      }
    } catch (_) {}
  }, [routeCoords, origin, destination, map]);

  return null;
}

function fmtDist(m) {
  return m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${Math.round(m)} m`;
}
function fmtTime(secs) {
  const m = Math.round(secs / 60);
  return m < 60 ? `${m} min` : `${Math.floor(m / 60)}h ${m % 60}min`;
}

export default function OrderRouteMap({
  ryderLocation,
  pickupLocation,
  deliveryLocation,
  mode, // "pickup" | "delivery"
}) {
  const [routeCoords, setRouteCoords] = useState([]);
  const [routeInfo,   setRouteInfo]   = useState(null);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState(false);

  const isPickup    = mode === "pickup";
  const origin      = isPickup ? ryderLocation  : pickupLocation;
  const destination = isPickup ? pickupLocation : deliveryLocation;
  const lineColor   = isPickup ? "#f59e0b" : "#7c3aed";
  const accent      = isPickup ? "amber" : "violet";

  useEffect(() => {
    if (!origin?.lat || !destination?.lat) return;
    let cancelled = false;
    setLoading(true);
    setError(false);
    setRouteCoords([]);
    setRouteInfo(null);

    fetch(
      `https://router.project-osrm.org/route/v1/driving/` +
      `${origin.lng},${origin.lat};${destination.lng},${destination.lat}` +
      `?overview=full&geometries=geojson`
    )
      .then(r => r.json())
      .then(data => {
        if (cancelled) return;
        const route = data.routes?.[0];
        if (route?.geometry?.coordinates) {
          setRouteCoords(route.geometry.coordinates.map(([lng, lat]) => [lat, lng]));
          setRouteInfo({ distance: route.distance, duration: route.duration });
        } else {
          setError(true);
        }
      })
      .catch(() => { if (!cancelled) setError(true); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [origin?.lat, origin?.lng, destination?.lat, destination?.lng]);

  if (!origin?.lat || !destination?.lat) return null;

  const centerLat = (origin.lat + destination.lat) / 2;
  const centerLng = (origin.lng + destination.lng) / 2;

  return (
    <div className="space-y-2.5">
      {/* Distancia y tiempo */}
      {routeInfo && (
        <div className={`flex items-center gap-4 px-3 py-2 rounded-xl bg-${accent}-500/10 border border-${accent}-500/20`}>
          <span className={`flex items-center gap-1.5 text-xs font-black text-${accent}-400`}>
            📍 {fmtDist(routeInfo.distance)}
          </span>
          <span className={`flex items-center gap-1.5 text-xs font-black text-${accent}-400`}>
            ⏱ {fmtTime(routeInfo.duration)}
          </span>
          {loading && <span className="text-xs text-white/30 ml-auto">Actualizando…</span>}
        </div>
      )}
      {!routeInfo && loading && (
        <div className="h-8 rounded-xl bg-white/5 animate-pulse" />
      )}

      {/* Botón navegación */}
      <button
        onClick={() => openMapsNavigation(destination.lat, destination.lng, origin.lat, origin.lng)}
        className={`w-full flex items-center justify-center gap-2 py-3 rounded-xl font-black text-sm transition-all active:scale-[0.98] ${
          isPickup
            ? "bg-amber-500 text-black shadow-lg shadow-amber-500/25"
            : "bg-violet-600 text-white shadow-lg shadow-violet-500/25"
        }`}
      >
        🧭 Iniciar navegación
      </button>

      {/* Mapa — isolation: isolate contiene el stacking context de Leaflet */}
      <div
        style={{
          height: 220,
          borderRadius: 14,
          overflow: "hidden",
          border: "1px solid rgba(255,255,255,0.08)",
          position: "relative",
          isolation: "isolate",   /* evita que Leaflet filtre z-index al exterior */
        }}
      >
        <MapContainer
          center={[centerLat, centerLng]}
          zoom={14}
          style={{ height: "100%", width: "100%" }}
          zoomControl={false}
          scrollWheelZoom={false}
          attributionControl={false}
        >
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          {routeCoords.length > 1 && (
            <Polyline positions={routeCoords} color={lineColor} weight={4} opacity={0.9} />
          )}
          <Marker position={[origin.lat, origin.lng]}
            icon={emojiIcon(isPickup ? "🛵" : "📦", lineColor, 30)} />
          <Marker position={[destination.lat, destination.lng]}
            icon={emojiIcon(isPickup ? "🏪" : "🏠", isPickup ? "#22c55e" : "#ef4444", 32)} />
          <MapFitter routeCoords={routeCoords} origin={origin} destination={destination} />
        </MapContainer>
      </div>

      {error && (
        <p className="text-xs text-center text-white/30">
          No se pudo calcular la ruta. Usa el botón de navegación.
        </p>
      )}
    </div>
  );
}
